import ReactMarkdown from 'react-markdown'

import Link from './link'

import { Separator } from '@/components/ui/separator'

function Markdown({ text }: { text: string }) {
    return (
        <div className="w-full text-foreground">
            <ReactMarkdown
                components={{
                    h1: ({ children }) => (
                        <h1 className="text-2xl font-extrabold tracking-tight mt-6 mb-3 first:mt-0 text-foreground">
                            {children}
                        </h1>
                    ),
                    h2: ({ children }) => (
                        <h2 className="text-xl font-bold tracking-tight mt-5 mb-2 first:mt-0 text-foreground">
                            {children}
                        </h2>
                    ),
                    h3: ({ children }) => (
                        <h3 className="text-lg font-semibold mt-4 mb-2 first:mt-0 text-foreground">
                            {children}
                        </h3>
                    ),
                    h4: ({ children }) => (
                        <h4 className="text-base font-semibold mt-3 mb-1 first:mt-0 text-foreground">
                            {children}
                        </h4>
                    ),
                    h5: ({ children }) => (
                        <h5 className="text-sm font-semibold mt-2 mb-1 text-foreground">
                            {children}
                        </h5>
                    ),
                    h6: ({ children }) => (
                        <h6 className="text-xs font-semibold mt-2 mb-1 text-foreground">
                            {children}
                        </h6>
                    ),
                    p: ({ children }) => (
                        <p className="text-base leading-relaxed mb-3 last:mb-0 text-foreground">
                            {children}
                        </p>
                    ),
                    a: ({ children, href }) => (
                        <Link
                            to={href || ''}
                            className="text-primary font-medium underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
                        >
                            {children}
                        </Link>
                    ),
                    blockquote: ({ children }) => (
                        <blockquote className="border-l-[3px] border-primary pl-4 py-0.5 my-3 text-muted-foreground">
                            {children}
                        </blockquote>
                    ),
                    strong: ({ children }) => (
                        <strong className="font-semibold text-foreground">
                            {children}
                        </strong>
                    ),
                    em: ({ children }) => (
                        <em className="italic">{children}</em>
                    ),
                    hr: () => <Separator className="my-5" />,
                    ul: ({ children }) => (
                        <ul className="list-disc pl-5 mb-3 space-y-1.5 text-base text-foreground">
                            {children}
                        </ul>
                    ),
                    ol: ({ children }) => (
                        <ol className="list-decimal pl-5 mb-3 space-y-1.5 text-base text-foreground">
                            {children}
                        </ol>
                    ),
                    li: ({ children }) => (
                        <li className="leading-relaxed pl-1">{children}</li>
                    ),
                    pre: ({ children }) => (
                        <pre className="bg-muted rounded-lg p-3 my-3 overflow-x-auto text-sm font-mono">
                            {children}
                        </pre>
                    ),
                    code: ({ children, className }) => {
                        const isBlock = !!className
                        return isBlock ? (
                            <code className={className}>{children}</code>
                        ) : (
                            <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                                {children}
                            </code>
                        )
                    },
                    table: ({ children }) => (
                        <div className="overflow-x-auto my-4 rounded-lg border border-border">
                            <table className="w-full text-sm">{children}</table>
                        </div>
                    ),
                    thead: ({ children }) => (
                        <thead className="bg-muted border-b border-border">
                            {children}
                        </thead>
                    ),
                    tbody: ({ children }) => (
                        <tbody className="divide-y divide-border">
                            {children}
                        </tbody>
                    ),
                    th: ({ children }) => (
                        <th className="px-4 py-2 text-left font-semibold text-foreground">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="px-4 py-2 text-foreground">
                            {children}
                        </td>
                    )
                }}
            >
                {text}
            </ReactMarkdown>
        </div>
    )
}

export default Markdown
